import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Database, Download, RefreshCw, Clock, CheckCircle2, AlertTriangle, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

export default function Backups() {
  const { user } = useContext(AuthContext);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/backups`, { withCredentials: true });
      setBackups(r.data || []);
    } catch {
      toast.error('Failed to load backups');
    }
    setLoading(false);
  };

  useEffect(() => { fetchBackups(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/backups`, {}, { withCredentials: true });
      if (r.data?.status === 'completed') {
        toast.success(`Backup created · ${r.data.record_count} records · ${formatBytes(r.data.size_bytes)}`);
      } else {
        toast.error(`Backup failed: ${r.data?.error || 'unknown error'}`);
      }
      await fetchBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed');
    }
    setCreating(false);
  };

  const download = (b) => {
    if (!b.file_id) { toast.error('No file attached'); return; }
    window.open(`${BACKEND}/api/files/${b.file_id}`, '_blank');
  };

  const remove = async (b) => {
    if (!window.confirm('Delete this backup record? (The stored file is retained in object storage and can be restored by the platform team.)')) return;
    try {
      await axios.delete(`${API}/backups/${b.id}`, { withCredentials: true });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const restore = async (b) => {
    try {
      // Step 1: preview
      const preview = await axios.post(`${API}/backups/${b.id}/restore`, {}, { withCredentials: true });
      const rows = preview.data?.would_restore || {};
      const total = Object.values(rows).reduce((a, v) => a + v, 0);
      const confirmMsg = `Restore ${total} records from this backup?\n\n${Object.entries(rows).filter(([, v]) => v > 0).map(([k, v]) => `  • ${k}: ${v}`).join('\n')}\n\nWARNING: All current data in these collections for your tenant will be WIPED and replaced.\n\nType YES in the next prompt to confirm.`;
      if (!window.confirm(confirmMsg)) return;
      const phrase = window.prompt('Type YES to confirm restore:');
      if ((phrase || '').trim().toUpperCase() !== 'YES') { toast.info('Restore cancelled'); return; }
      const result = await axios.post(`${API}/backups/${b.id}/restore`, { confirm: true }, { withCredentials: true });
      toast.success(`Restored ${result.data.restored_rows} records (wiped ${result.data.wiped_rows}). Reloading…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };

  const canManage = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role);

  return (
    <Layout>
      <div data-testid="backups-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Database className="w-6 h-6 text-amber-600" />Data Backups
            </h1>
            <p className="text-sm text-muted-foreground">Tenant-scoped JSON snapshots of every collection. Auto-backup runs every 24 hours.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="refresh-backups-btn" variant="outline" onClick={fetchBackups}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            {canManage && (
              <Button data-testid="create-backup-btn" onClick={createBackup} disabled={creating} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Database className="w-4 h-4 mr-1" />{creating ? 'Backing up…' : 'Create Backup Now'}
              </Button>
            )}
          </div>
        </div>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          ) : backups.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Database className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              No backups yet. Click <strong>Create Backup Now</strong> to take the first snapshot.
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {backups.map((b, idx) => (
                <div key={b.id} data-testid={`backup-row-${idx}`} className="flex flex-wrap items-center gap-4 p-4 hover:bg-slate-50/60">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${b.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {b.status === 'completed' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-foreground">{b.type === 'auto' ? 'Auto-backup' : 'Manual backup'}</span>
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${b.type === 'auto' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}`}>{b.type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(b.created_at).toLocaleString()}</span>
                      <span>·</span>
                      <span>{b.record_count} records · {formatBytes(b.size_bytes)}</span>
                      {b.triggered_by_email && <span>· by {b.triggered_by_email}</span>}
                    </div>
                    {b.error && <div className="text-xs text-rose-600 mt-1">Error: {b.error}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {b.file_id && (
                      <Button data-testid={`download-backup-${idx}`} size="sm" variant="outline" onClick={() => download(b)}>
                        <Download className="w-4 h-4 mr-1" />Download JSON
                      </Button>
                    )}
                    {canManage && b.status === 'completed' && b.file_id && (
                      <Button data-testid={`restore-backup-${idx}`} size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => restore(b)}>
                        <RotateCcw className="w-4 h-4 mr-1" />Restore
                      </Button>
                    )}
                    {canManage && (
                      <Button data-testid={`delete-backup-${idx}`} size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => remove(b)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 bg-amber-50/50 border-amber-200 text-sm text-amber-900">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">About Backups</div>
              <ul className="list-disc ml-5 space-y-1 text-amber-800">
                <li>Backups are JSON dumps of all your company-scoped collections — users, buildings, assets, complaints, service reports, stock, audit logs, etc.</li>
                <li>Automatic backups run once per day (every 24 hours) for every active company.</li>
                <li>Only your own tenant's data is included. Multi-tenant isolation is preserved.</li>
                <li>Download a backup to restore outside the platform, or contact support for an in-place restore.</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
